import { useState } from 'react';
import Link from 'next/link';
import grid_lines from '../../public/images/predicted-points-grid-lines.png';
import {
  BodyRegularBold,
  BodySmallRegular,
  BodySmallRegularBold,
  CaptionRegular,
  SubtextBold
} from '@components/atoms/typography';
import { getPredictedPointsLevelStyles } from '@components/fixtures/utils/fixtureTypes';
import Icon from '@components/atoms/icon';
import PlayerFaceWithShirtFallback from '@components/player/face-with-shirt-fallback';
import useWindowSize from '@hooks/useWindowSize';
import { useMarketingCopy } from '@hooks/useMarketingCopy';
import type { Player } from '@api-service/players';

const POSITION_LABELS: Record<
  Player['position'],
  { short: string; full: string }
> = {
  GK: { short: 'GK', full: 'Goalkeeper' },
  DEF: { short: 'DEF', full: 'Defender' },
  MID: { short: 'MID', full: 'Midfielder' },
  FWD: { short: 'FWD', full: 'Forward' }
};

const calculateAbsoluteBottomPercentage = (
  points: number,
  isMobile: boolean,
  fixtureCount: number
): number => {
  const isMultiFixture = fixtureCount > 1;
  if (isMobile && isMultiFixture && points > 5) return 16;
  if (isMultiFixture && points > 5) return 38;
  if (isMobile && points > 8) return 26;
  if (points > 10) return 50;
  return isMobile
    ? Math.round((points / 18) * 100) - 20
    : Math.round((points / 18) * 100);
};

type PlayerPredictedPointsCardProps = {
  player: Player;
  isMobile: boolean;
  isAuthorisedToViewRestrictedPageContent: boolean;
};

const PlayerPredictedPointsCard = ({
  player,
  isMobile,
  isAuthorisedToViewRestrictedPageContent
}: PlayerPredictedPointsCardProps) => {
  const [show, setShow] = useState(!isMobile);
  const { width } = useWindowSize();
  const copy = useMarketingCopy();

  const toggleShow = () => {
    if (!isMobile) return;
    setShow(!show);
  };

  const positionLabel = POSITION_LABELS[player.position];
  const ownershipDisplay =
    player.ownership != null ? `${player.ownership.toFixed(1)}%` : '-';
  const pointsPerStartDisplay = player.aggregates.pointsPerStart.toFixed(1);
  const totalPredictedPoints = player.fixtures
    .reduce(
      (accumulator, fixture) => accumulator + fixture.predictions.points,
      0
    )
    .toFixed(1);

  const fixturesByGameweek = player.fixtures.reduce<
    Record<number, Player['fixtures']>
  >((accumulator, fixture) => {
    const bucket = accumulator[fixture.gameweek] ?? [];
    bucket.push(fixture);
    accumulator[fixture.gameweek] = bucket;
    return accumulator;
  }, {});

  const gameweekGroups = Object.keys(fixturesByGameweek)
    .map((gameweek) => Number(gameweek))
    .sort((a, b) => a - b)
    .map((gameweek) => {
      const fixtures = fixturesByGameweek[gameweek];
      const points = fixtures.reduce(
        (sum, fixture) => sum + fixture.predictions.points,
        0
      );
      const minutes = fixtures.reduce(
        (sum, fixture) => sum + fixture.predictions.minutes,
        0
      );
      return { gameweek, fixtures, points, minutes };
    });

  const getFixtures = () =>
    gameweekGroups.map(({ gameweek, fixtures, points, minutes }) => {
      const predictedPointsStyles = getPredictedPointsLevelStyles(points);
      const bottomForPredictedPoints = calculateAbsoluteBottomPercentage(
        points,
        isMobile,
        fixtures.length
      );

      return (
        <div
          key={`${player.id}-${gameweek}`}
          className="mx-0 sm:mx-1 text-center relative h-[88px]"
        >
          <div className="absolute top-[-4px] left-[-2px] md:top-[-20px] md:left-[-2px] lg:top-[-24px] flex w-[32px] lg:left-[-18px]">
            <CaptionRegular>GW {gameweek}</CaptionRegular>
          </div>
          <div
            className="absolute"
            style={{
              bottom: `${bottomForPredictedPoints}%`,
              left: isMobile ? '-8px' : '-24px'
            }}
          >
            <div
              className={`cursor-default px-1 sm:px-2 py-1 rounded-lg ${predictedPointsStyles} w-[42px]`}
            >
              <BodySmallRegularBold>{points.toFixed(1)}</BodySmallRegularBold>
              {fixtures.map((fixture, fixtureIndex) => (
                <SubtextBold
                  key={`${fixture.opponent.shortName}-${fixtureIndex}`}
                  className="uppercase block"
                >
                  {fixture.opponent.shortName}
                  {fixture.isHome ? '(H)' : '(A)'}
                </SubtextBold>
              ))}
              <SubtextBold>{Math.round(minutes)}'</SubtextBold>
            </div>
          </div>
        </div>
      );
    });

  return (
    <div
      className="pt-3 pb-3 md:pt-7 px-1 sm:px-4 mb-4 cursor-pointer md:cursor-auto rounded-lg shadow-100 overflow-x-hidden"
      onClick={toggleShow}
    >
      <div className="flex flex-col lg:flex-row justify-start lg:justify-evenly">
        <div className="flex flex-col lg:flex-row">
          <div className="flex mt-2 justify-evenly lg:justify-start items-center">
            <div className="w-[48px] h-[48px] md:w-[72px] md:h-[72px] flex justify-center items-center relative">
              <PlayerFaceWithShirtFallback
                player={player}
                size={isMobile ? 48 : 72}
              />
            </div>
            <div className="flex flex-col px-1 justify-center w-[140px] lg:w-[200px]">
              <BodyRegularBold className="flex items-center w-[140px]">
                {isMobile && player.displayName.length > 10
                  ? `${player.displayName.substring(0, 10)}...`
                  : player.displayName}
              </BodyRegularBold>
              <div className="flex items-center mt-1 mb-2">
                <BodySmallRegular
                  textColor="text-neutral-600"
                  className="text-sm mb-0 mx-1 whitespace-nowrap overflow-hidden text-ellipsis"
                >
                  {player.team.fullName}
                </BodySmallRegular>
                <div className="h-1 w-1 mx-1 bg-[#D9D9D9] rounded-full" />
                <BodySmallRegular className="ml-1" textColor="text-neutral-600">
                  {isMobile ? positionLabel.short : positionLabel.full}
                </BodySmallRegular>
              </div>
            </div>
            <div className="flex lg:hidden justify-center flex-col items-center space-y-1 bg-primary-100 px-2 py-2 rounded-lg">
              <BodySmallRegular className="text-center">
                Predicted
              </BodySmallRegular>
              <BodySmallRegularBold>
                {totalPredictedPoints}
              </BodySmallRegularBold>
            </div>
            <div className="flex flex-col items-center space-y-1 lg:hidden">
              <BodySmallRegular>Price</BodySmallRegular>
              <BodySmallRegularBold>
                £{player.price.toFixed(1)}m
              </BodySmallRegularBold>
            </div>
          </div>
          {Boolean(show) && (
            <div className="flex flex-col lg:flex-row">
              <div className="flex mb-4 md:mb-0 space-x-7 justify-evenly items-center py-3 lg:py-0">
                <div className="flex-col items-center space-y-1 hidden lg:flex">
                  <BodySmallRegular>Price</BodySmallRegular>
                  <BodySmallRegularBold>
                    £{player.price.toFixed(1)}m
                  </BodySmallRegularBold>
                </div>
                <div className="flex flex-col items-center space-y-1">
                  <BodySmallRegular>Ownership</BodySmallRegular>
                  <BodySmallRegularBold>
                    {ownershipDisplay}
                  </BodySmallRegularBold>
                </div>
                <div className="flex flex-col items-center space-y-1">
                  <BodySmallRegular className="text-center">
                    Pts / start
                  </BodySmallRegular>
                  <BodySmallRegularBold>
                    {pointsPerStartDisplay}
                  </BodySmallRegularBold>
                </div>
                <div className="hidden lg:flex flex-col items-center space-y-1 bg-primary-100 px-3 py-2 rounded-lg text-center">
                  <BodySmallRegular className="text-center">
                    Predicted
                  </BodySmallRegular>
                  <BodySmallRegularBold>
                    {totalPredictedPoints}
                  </BodySmallRegularBold>
                </div>
              </div>
            </div>
          )}
        </div>
        {Boolean(show) && (
          <div className="relative flex flex-1 justify-center transition-all pl-1 pr-1 md:pl-7 md:pr-3">
            {!isAuthorisedToViewRestrictedPageContent && (
              <div
                style={{
                  background: `linear-gradient(270deg, #FFF ${
                    isMobile ? '75%' : '50%'
                  }, rgba(255, 255, 255, 0.00) 96.89%)`
                }}
                className="absolute -top-4 right-0 bg-white z-30 h-[125%] w-64 p-4 flex justify-end"
              >
                <div className="w-[75%] flex flex-col justify-center">
                  <div className="flex items-center mb-1">
                    <Icon name="Premium" className="h-4 w-4 mr-1" />
                    <BodySmallRegularBold>
                      This is a members-only feature
                    </BodySmallRegularBold>
                  </div>
                  <CaptionRegular>
                    <Link
                      href="/join"
                      className="pointer text-primary-600 font-bold"
                    >
                      {copy.predictedPointsCard.subtitle}
                    </Link>{' '}
                    and get access to Points Predictions.
                  </CaptionRegular>
                </div>
              </div>
            )}
            <div
              style={{
                backgroundImage: `url(${grid_lines.src})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                transform: isMobile ? 'translateX(-8px)' : 'translateX(8px)'
              }}
              className="mt-3 sm:mt-8 lg:mt-0 flex h-[88px] flex-grow justify-evenly sm:justify-between min-w-[420px] max-w-[800px] px-5 px-2 lg:px-6 py-5 md:py-0 items-end space-x-1"
            >
              {width > 0 && getFixtures()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerPredictedPointsCard;
